import React, { useState } from "react";
import { api, formatErr, fmtMoney, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Sparkles, Upload, Loader2, FileImage } from "lucide-react";

export default function AIImportProducts({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [products, setProducts] = useState(null);
  const [selected, setSelected] = useState({});
  const [importing, setImporting] = useState(false);

  const reset = () => { setFile(null); setProducts(null); setSelected({}); };

  const extract = async () => {
    if (!file) return toast.error("اختر ملف صورة أو PDF");
    setExtracting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const cid = localStorage.getItem("company_id"); const token = localStorage.getItem("access_token");
      const res = await fetch(`${API}/ai/import-products`, { method: "POST", body: fd, credentials: "include", headers: { "X-Company-Id": cid, "Authorization": `Bearer ${token}` }});
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "فشل الاستخراج");
      if (data.count === 0) { toast.info("لم يتم العثور على منتجات في الملف"); }
      else { toast.success(`تم استخراج ${data.count} منتج — راجعها ثم أضفها`); }
      setProducts(data.products);
      const sel = {}; data.products.forEach((_, i) => sel[i] = true); setSelected(sel);
    } catch(e) { toast.error(e.message); }
    finally { setExtracting(false); }
  };

  const importSelected = async () => {
    const chosen = products.filter((_, i) => selected[i]);
    if (chosen.length === 0) return toast.error("اختر منتجاً واحداً على الأقل");
    setImporting(true);
    try {
      const { data } = await api.post("/products/bulk", { products: chosen });
      toast.success(`تم إضافة ${data.created} منتج`);
      reset(); onClose(); onDone?.();
    } catch(e) { toast.error(formatErr(e)); }
    finally { setImporting(false); }
  };

  const updateProd = (i, patch) => setProducts(ps => ps.map((p, x) => x === i ? {...p, ...patch} : p));

  return (
    <Dialog open={open} onOpenChange={(o)=>{if(!o){reset(); onClose();}}}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="ai-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" /> استيراد منتجات بالذكاء الاصطناعي</DialogTitle>
          <DialogDescription>حمّل صورة فاتورة أو كتالوج أو قائمة أسعار (JPG/PNG/PDF) — الذكاء الاصطناعي سيستخرج المنتجات تلقائياً.</DialogDescription>
        </DialogHeader>

        {!products && (
          <div className="space-y-4">
            <label className="border-2 border-dashed border-border rounded-md p-8 flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-accent/50 transition-colors">
              <FileImage className="w-10 h-10 text-muted-foreground mb-3" />
              <div className="font-semibold">{file ? file.name : "اختر ملف (JPG / PNG / PDF)"}</div>
              <div className="text-xs text-muted-foreground mt-1">فاتورة مورد، قائمة أسعار، كتالوج...</div>
              <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" hidden data-testid="ai-import-file-input"
                onChange={(e)=>setFile(e.target.files?.[0] || null)} />
            </label>
            <Button onClick={extract} disabled={!file || extracting} className="w-full" data-testid="ai-extract-button" size="lg">
              {extracting ? <><Loader2 className="w-4 h-4 me-2 animate-spin" /> جاري الاستخراج بالذكاء الاصطناعي...</> : <><Sparkles className="w-4 h-4 me-2" /> استخراج المنتجات</>}
            </Button>
          </div>
        )}

        {products && (
          <div className="space-y-3">
            <div className="text-sm">
              <b>{products.length}</b> منتج مُستخرج — راجع/عدّل التفاصيل ثم اضغط «إضافة المحدد».
              <span className="text-xs text-muted-foreground me-2">({Object.values(selected).filter(Boolean).length} محدد)</span>
            </div>
            <div className="border border-border rounded-md overflow-hidden max-h-[50vh] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-card z-10">
                  <TableRow>
                    <TableHead className="w-10"><Checkbox checked={products.every((_,i)=>selected[i])} onCheckedChange={(v)=>{const s={}; products.forEach((_,i)=>s[i]=!!v); setSelected(s);}} /></TableHead>
                    <TableHead>الاسم</TableHead>
                    <TableHead>الباركود</TableHead>
                    <TableHead>التكلفة</TableHead>
                    <TableHead>البيع</TableHead>
                    <TableHead>العملة</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {products.map((p, i) => (
                    <TableRow key={i} data-testid={`extracted-row-${i}`}>
                      <TableCell><Checkbox checked={!!selected[i]} onCheckedChange={(v)=>setSelected(s=>({...s, [i]: !!v}))} data-testid={`select-${i}`} /></TableCell>
                      <TableCell><Input value={p.name} onChange={(e)=>updateProd(i,{name:e.target.value})} className="h-8" /></TableCell>
                      <TableCell><Input value={p.barcode||""} onChange={(e)=>updateProd(i,{barcode:e.target.value})} className="h-8 font-mono text-xs" /></TableCell>
                      <TableCell><Input type="number" value={p.cost_price} onChange={(e)=>updateProd(i,{cost_price:+e.target.value})} className="h-8 w-24 tabular-nums" /></TableCell>
                      <TableCell><Input type="number" value={p.sale_price} onChange={(e)=>updateProd(i,{sale_price:+e.target.value})} className="h-8 w-24 tabular-nums" /></TableCell>
                      <TableCell className="font-mono text-xs">{p.currency}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        <DialogFooter>
          {products && <Button variant="outline" onClick={reset}>ملف آخر</Button>}
          {products && <Button onClick={importSelected} disabled={importing} data-testid="ai-import-confirm-button">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 me-1" /> إضافة المحدد ({Object.values(selected).filter(Boolean).length})</>}
          </Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
